import React from 'react';
import Button from '@/components/UI/Button';
import { StatusBadge } from '@/components/UI';
import type { LinkedTradingAccountRow, PortfolioCapitalAllocationDisplayRow } from './types';
import { formatCurrency } from './helpers';

interface ExecutionCapitalBannerProps {
    linkedTradingAccount: LinkedTradingAccountRow;
    executionCapitalRow: PortfolioCapitalAllocationDisplayRow | null;
    hasCapitalAllocationAlert: boolean;
    onViewDetails: () => void;
    onManageAllocation: () => void;
}

const ExecutionCapitalBanner: React.FC<ExecutionCapitalBannerProps> = ({
    linkedTradingAccount,
    executionCapitalRow,
    hasCapitalAllocationAlert,
    onViewDetails,
    onManageAllocation
}) => {
    return (
        <div className="grid grid-cols-12 gap-4 mb-4">
            <div className="col-span-12">
                <div className="rounded-lg border border-zinc-800 bg-[#151515] p-3 text-white shadow-md">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <h5 className="text-base font-medium">Brokerage limits</h5>
                                {hasCapitalAllocationAlert && (
                                    <StatusBadge status="needs_review" tone="warning">
                                        Needs Review
                                    </StatusBadge>
                                )}
                            </div>
                            <div className="mt-1 text-sm text-zinc-400">
                                {executionCapitalRow
                                    ? `${executionCapitalRow.label} reserved through ${linkedTradingAccount.name}${
                                          linkedTradingAccount.displayAccountId ? ` (${linkedTradingAccount.displayAccountId})` : ''
                                      }`
                                    : `No execution capital allocated through ${linkedTradingAccount.name}`}
                            </div>
                        </div>

                        <div className="text-sm">
                            <div>
                                <div className="text-xs uppercase tracking-wider text-zinc-500">Status</div>
                                <div className={`mt-1 ${executionCapitalRow?.isSupported === false ? 'text-yellow-300' : 'text-zinc-200'}`}>
                                    {executionCapitalRow
                                        ? executionCapitalRow.overAllocatedAmount > 0
                                            ? `Over capacity by ${formatCurrency(executionCapitalRow.overAllocatedAmount)}`
                                            : executionCapitalRow.isSupported
                                              ? 'Supported'
                                              : (executionCapitalRow.supportReason ?? 'Unsupported')
                                        : 'Unallocated'}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2 lg:justify-end">
                            <Button size="sm" variant="secondary" onClick={onViewDetails}>
                                View brokerage details
                            </Button>
                            <Button size="sm" variant="primary" onClick={onManageAllocation}>
                                Manage Allocation
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExecutionCapitalBanner;
